import type { ScopedThreadRef } from "@cmmd-forge/contracts";

import { readEnvironmentApi } from "../../environmentApi";
import { newCommandId } from "../../lib/utils";
import { toastManager } from "../ui/toast";
import type { DestructiveConfirmHandle } from "./useDestructiveConfirm";

interface StartFreshProviderSessionInput {
  readonly threadRef: ScopedThreadRef;
  readonly confirm: DestructiveConfirmHandle["confirm"];
}

export async function startFreshProviderSession({
  threadRef,
  confirm,
}: StartFreshProviderSessionInput): Promise<void> {
  const confirmed = await confirm({
    title: "Start a fresh provider session?",
    description:
      "Forge keeps this thread's visible history, but the provider will not resume its previous private session context. Your next message starts a newly account-bound provider session.",
    confirmLabel: "Start fresh session",
  });
  if (!confirmed) return;

  const environmentApi = readEnvironmentApi(threadRef.environmentId);
  if (!environmentApi) return;

  try {
    await environmentApi.orchestration.dispatchCommand({
      type: "thread.session.stop",
      commandId: newCommandId(),
      threadId: threadRef.threadId,
      createdAt: new Date().toISOString(),
    });
    toastManager.add({
      type: "success",
      title: "Fresh provider session ready",
      description: "Your next message will start a newly account-bound provider session.",
    });
  } catch (error) {
    toastManager.add({
      type: "error",
      title: "Unable to start fresh provider session",
      description: error instanceof Error ? error.message : "An error occurred.",
    });
  }
}
